<script lang="ts" setup>
import datepicker from 'vue3-datepicker'
import type { FieldData } from '~~/server/api/forms/form'
import { sk } from 'date-fns/locale'
import { FormKitNode } from '@formkit/core'
export type CalendarView = 'time' | 'day' | 'month' | 'year'

export interface FormKitCustomDateProps {
    data: FieldData
    showResetBtn?: boolean
    startView?: CalendarView
    upperLimit?: Date
    lowerLimit?: Date
}

export interface FormKitCustomDateEmits {
    // eslint-disable-next-line no-unused-vars
    (e: 'update', value: any): void
}

const emit = defineEmits<FormKitCustomDateEmits>()
const props = withDefaults(defineProps<FormKitCustomDateProps>(), {
    showResetBtn: true,
    startView: 'day',
    upperLimit: undefined,
    lowerLimit: undefined
})

const { $date } = useNuxtApp()

const model = ref<Date | undefined>(
    props.data.defaultValue
        ? props.data.defaultValue instanceof Date
            ? props.data.defaultValue
            : new Date(props.data.defaultValue)
        : undefined
)
const config = useRuntimeConfig()
const formattedDate = ref<string | undefined>(
    model.value
        ? $date(model.value.toISOString(), config.public.CHART_DATE_FORMAT)
        : undefined
)
const userInteracted = ref<boolean>(false)

const isInvalid = computed(() => {
    const node: FormKitNode = input.value?.node

    if(!node) return false

    return !node.context?.state?.valid && node.context?.state?.validationVisible
})

const opened = ref<boolean>(false)

const input = ref()
const datepickerWrapper = ref<HTMLElement | null>(null)

// Keyboard navigation: index of focused button in the current view grid
const focusedIndex = ref<number>(-1)

// Focus zones: nav-left (0), heading (1), nav-right (2), grid (3)
type FocusZone = 'nav-left' | 'heading' | 'nav-right' | 'grid'
const ZONE_ORDER: FocusZone[] = ['nav-left', 'heading', 'nav-right', 'grid']
const activeZone = ref<FocusZone>('grid')

// Prevents the datepicker from closing while keyboard-navigating
const navigatingCalendar = ref<boolean>(false)

const isRequired = computed(() => props.data.required ||
    props.data.validations?.includes('required'))

// Detect which view is currently visible: year (3 cols), month (3 cols), day (7 cols)
type ViewInfo = { buttons: HTMLButtonElement[]; cols: number; view: 'year' | 'month' | 'day' }

const getVisiblePopout = (): HTMLElement | null => {
    if (!datepickerWrapper.value) return null
    const popouts = datepickerWrapper.value.querySelectorAll('.v3dp__popout')
    for (const popout of popouts) {
        if ((popout as HTMLElement).style.display === 'none') continue
        return popout as HTMLElement
    }
    return null
}

const getVisibleView = (): ViewInfo | null => {
    const popout = getVisiblePopout()
    if (!popout) return null

    const classList = popout.classList
    const buttons = Array.from(
        popout.querySelectorAll('.v3dp__elements button')
    ) as HTMLButtonElement[]

    if (buttons.length === 0) return null

    if (classList.contains('v3dp__popout-year')) {
        return { buttons, cols: 3, view: 'year' }
    }
    if (classList.contains('v3dp__popout-month')) {
        return { buttons, cols: 3, view: 'month' }
    }
    if (classList.contains('v3dp__popout-day')) {
        return { buttons, cols: 7, view: 'day' }
    }
    return null
}

const getZoneElement = (zone: FocusZone): HTMLButtonElement | null => {
    const popout = getVisiblePopout()
    if (!popout) return null

    switch (zone) {
    case 'nav-left':
        return popout.querySelector('.v3dp__heading__button__left') as HTMLButtonElement
    case 'heading':
        return popout.querySelector('.v3dp__heading__center') as HTMLButtonElement
    case 'nav-right':
        return popout.querySelector('.v3dp__heading__button__right') as HTMLButtonElement
    default:
        return null
    }
}

// Screen reader announcement for focused item
const screenReaderAnnouncement = ref<string>('')

const announce = (message: string): void => {
    screenReaderAnnouncement.value = '\u00A0'
    nextTick(() => {
        screenReaderAnnouncement.value = message
    })
}

const getPopoutHeading = (): string => {
    const popout = getVisiblePopout()
    if (!popout) return ''
    return popout.querySelector('.v3dp__heading__center')?.textContent?.trim() || ''
}

const announceGridItem = (btn: HTMLButtonElement, view: ViewInfo): void => {
    const text = btn.querySelector('span')?.textContent?.trim() || btn.textContent?.trim() || ''
    const heading = getPopoutHeading()

    if (view.view === 'day') {
        // e.g. "23, apríl 2026" — day number + month/year from heading
        announce(heading ? `${text}, ${heading}` : text)
    } else if (view.view === 'month') {
        // e.g. "apr, mesiac, 2026"
        announce(heading ? `${text}, mesiac, ${heading}` : `${text}, mesiac`)
    } else {
        // year: e.g. "2026, rok"
        announce(`${text}, rok`)
    }
}

const clearHighlights = (): void => {
    datepickerWrapper.value
        ?.querySelectorAll('.v3dp__kbd-focus')
        .forEach((el) => el.classList.remove('v3dp__kbd-focus'))
}

const highlightZone = (): void => {
    clearHighlights()

    if (activeZone.value === 'grid') {
        const view = getVisibleView()
        if (!view) return
        const btn = view.buttons[focusedIndex.value]
        if (btn) {
            btn.classList.add('v3dp__kbd-focus')
            announceGridItem(btn, view)
        }
    } else {
        const view = getVisibleView()
        if (!view) return
        const el = getZoneElement(activeZone.value)
        if (el) {
            el.classList.add('v3dp__kbd-focus')
            switch (activeZone.value) {
            case 'nav-left':
                announce(view.view === 'day' ? 'Predchádzajúci mesiac' : 'Predchádzajúci rok')
                break
            case 'nav-right':
                announce(view.view === 'day' ? 'Nasledujúci mesiac' : 'Nasledujúci rok')
                break
            case 'heading':
                announce(el.textContent?.trim() || '')
                break
            }
        }
    }
}

const initFocusIndex = (): void => {
    const view = getVisibleView()
    if (!view) return

    let idx = view.buttons.findIndex((btn) => btn.classList.contains('selected'))
    if (idx === -1) idx = view.buttons.findIndex((btn) => btn.classList.contains('current'))
    if (idx === -1) idx = view.buttons.findIndex((btn) => !btn.disabled)
    focusedIndex.value = idx >= 0 ? idx : 0
    activeZone.value = 'grid'

    highlightZone()
}

const getDatepickerInput = (): HTMLInputElement | null => {
    return datepickerWrapper.value?.querySelector('.v3dp__datepicker input') as HTMLInputElement | null
}

const closeCalendar = (): void => {
    navigatingCalendar.value = false
    focusedIndex.value = -1
    activeZone.value = 'grid'
    const inputEl = getDatepickerInput()
    inputEl?.blur()
}

// Re-focus the datepicker input to keep calendar open while navigating
const keepCalendarOpen = (): void => {
    const inputEl = getDatepickerInput()
    if (inputEl && document.activeElement !== inputEl) {
        navigatingCalendar.value = true
        inputEl.focus()
    }
}

const handleTabNavigation = (e: KeyboardEvent): void => {
    e.preventDefault()
    e.stopPropagation()

    navigatingCalendar.value = true

    const currentZoneIdx = ZONE_ORDER.indexOf(activeZone.value)
    const direction = e.shiftKey ? -1 : 1
    let nextIdx = currentZoneIdx + direction

    if (nextIdx >= ZONE_ORDER.length) nextIdx = 0
    if (nextIdx < 0) nextIdx = ZONE_ORDER.length - 1

    // Skip disabled nav buttons
    const nextZone = ZONE_ORDER[nextIdx]!
    if (nextZone === 'nav-left' || nextZone === 'nav-right') {
        const el = getZoneElement(nextZone)
        if (el?.disabled) {
            nextIdx += direction
            if (nextIdx >= ZONE_ORDER.length) nextIdx = 0
            if (nextIdx < 0) nextIdx = ZONE_ORDER.length - 1
        }
    }

    activeZone.value = ZONE_ORDER[nextIdx]!
    highlightZone()
}

const handleGridKeydown = (e: KeyboardEvent): void => {
    const view = getVisibleView()
    if (!view) return

    const { buttons, cols } = view
    const total = buttons.length
    if (total === 0) return

    if (focusedIndex.value < 0 || focusedIndex.value >= total) {
        initFocusIndex()
    }

    let idx = focusedIndex.value

    switch (e.key) {
    case 'ArrowLeft':
        e.preventDefault()
        idx = Math.max(0, idx - 1)
        while (idx > 0 && buttons[idx]?.disabled) idx--
        break
    case 'ArrowRight':
        e.preventDefault()
        idx = Math.min(total - 1, idx + 1)
        while (idx < total - 1 && buttons[idx]?.disabled) idx++
        break
    case 'ArrowUp':
        e.preventDefault()
        idx = idx - cols
        if (idx < 0) {
            const leftBtn = getZoneElement('nav-left')
            if (leftBtn && !leftBtn.disabled) {
                leftBtn.click()
                nextTick(() => {
                    keepCalendarOpen()
                    const newView = getVisibleView()
                    if (newView) {
                        const col = focusedIndex.value % cols
                        idx = newView.buttons.length - cols + col
                        if (idx >= newView.buttons.length) idx = newView.buttons.length - 1
                        while (idx > 0 && newView.buttons[idx]?.disabled) idx--
                        focusedIndex.value = idx
                        highlightZone()
                    }
                })
                return
            }
            return
        }
        while (idx > 0 && buttons[idx]?.disabled) idx--
        break
    case 'ArrowDown':
        e.preventDefault()
        idx = idx + cols
        if (idx >= total) {
            const rightBtn = getZoneElement('nav-right')
            if (rightBtn && !rightBtn.disabled) {
                rightBtn.click()
                nextTick(() => {
                    keepCalendarOpen()
                    const newView = getVisibleView()
                    if (newView) {
                        const col = focusedIndex.value % cols
                        idx = col
                        while (idx < newView.buttons.length - 1 && newView.buttons[idx]?.disabled) idx++
                        focusedIndex.value = idx
                        highlightZone()
                    }
                })
                return
            }
            return
        }
        while (idx < total - 1 && buttons[idx]?.disabled) idx++
        break
    case 'PageUp':
        e.preventDefault()
        {
            const leftBtn = getZoneElement('nav-left')
            if (leftBtn && !leftBtn.disabled) {
                leftBtn.click()
                nextTick(() => {
                    keepCalendarOpen()
                    const newView = getVisibleView()
                    if (newView) {
                        focusedIndex.value = Math.min(focusedIndex.value, newView.buttons.length - 1)
                        highlightZone()
                    }
                })
            }
        }
        return
    case 'PageDown':
        e.preventDefault()
        {
            const rightBtn = getZoneElement('nav-right')
            if (rightBtn && !rightBtn.disabled) {
                rightBtn.click()
                nextTick(() => {
                    keepCalendarOpen()
                    const newView = getVisibleView()
                    if (newView) {
                        focusedIndex.value = Math.min(focusedIndex.value, newView.buttons.length - 1)
                        highlightZone()
                    }
                })
            }
        }
        return
    case 'Home':
        e.preventDefault()
        idx = 0
        while (idx < total - 1 && buttons[idx]?.disabled) idx++
        break
    case 'End':
        e.preventDefault()
        idx = total - 1
        while (idx > 0 && buttons[idx]?.disabled) idx--
        break
    default:
        return
    }

    if (idx < 0) idx = 0
    if (idx >= total) idx = total - 1
    if (buttons[idx]?.disabled) return

    focusedIndex.value = idx
    highlightZone()
}

const handleKeydown = (e: KeyboardEvent): void => {
    if (!opened.value) return

    // Tab navigates between focus zones (nav-left, heading, nav-right, grid)
    if (e.key === 'Tab') {
        handleTabNavigation(e)
        return
    }

    // Escape closes calendar from any zone
    if (e.key === 'Escape') {
        e.preventDefault()
        closeCalendar()
        return
    }

    // Enter/Space activates the focused zone
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (activeZone.value === 'grid') {
            const view = getVisibleView()
            if (!view) return
            const btn = view.buttons[focusedIndex.value]
            if (btn && !btn.disabled) {
                btn.click()
                nextTick(() => {
                    if (opened.value) {
                        keepCalendarOpen()
                        nextTick(() => initFocusIndex())
                    }
                })
            }
        } else {
            const el = getZoneElement(activeZone.value)
            if (el && !el.disabled) {
                el.click()
                nextTick(() => {
                    keepCalendarOpen()
                    if (activeZone.value === 'heading') {
                        nextTick(() => initFocusIndex())
                    } else {
                        const newView = getVisibleView()
                        if (newView) {
                            focusedIndex.value = Math.min(focusedIndex.value, newView.buttons.length - 1)
                            highlightZone()
                        }
                    }
                })
            }
        }
        return
    }

    // Arrow keys only work in grid zone
    if (activeZone.value === 'grid') {
        handleGridKeydown(e)
    }
}

// Attach keydown listener directly to the datepicker's input element
// because vue3-datepicker uses inheritAttrs:false and event bubbling
// from the internal input may not reach the wrapper div reliably.
onMounted(() => {
    const inputEl = getDatepickerInput()
    if (inputEl) {
        inputEl.addEventListener('keydown', handleKeydown)
    }
})

onUnmounted(() => {
    const inputEl = getDatepickerInput()
    if (inputEl) {
        inputEl.removeEventListener('keydown', handleKeydown)
    }
})

watch(opened, (isOpen) => {
    if (isOpen) {
        navigatingCalendar.value = false
        nextTick(() => {
            // Mark visible popout as dialog for screen readers
            const popout = getVisiblePopout()
            if (popout) {
                popout.setAttribute('role', 'dialog')
                popout.setAttribute('aria-modal', 'false')
                popout.setAttribute('aria-label', props.data.label || 'Kalendár')
            }

            // Announce calendar opened with current view context
            const heading = getPopoutHeading()
            const view = getVisibleView()
            const viewName = view?.view === 'year' ? 'Výber roka' : view?.view === 'month' ? 'Výber mesiaca' : 'Výber dátumu'
            announce(heading ? `${viewName}, ${heading}` : viewName)
            initFocusIndex()
        })
    } else {
        // If we're navigating, re-open the calendar
        if (navigatingCalendar.value) {
            navigatingCalendar.value = false
            nextTick(() => {
                const inputEl = getDatepickerInput()
                inputEl?.focus()
            })
            return
        }
        focusedIndex.value = -1
        activeZone.value = 'grid'
        screenReaderAnnouncement.value = ''
        clearHighlights()
    }
})

const handleChange = (value: Date | null | undefined): any => {
    userInteracted.value = true
    if(value) {
        formattedDate.value = $date(
            new Date(value).toISOString(),
            config.public.CHART_DATE_FORMAT
        )
        announce(`Vybraný dátum: ${formattedDate.value}`)
        emit('update', value)
    }
}

const handleClickReset = (): void => {
    userInteracted.value = true
    model.value = undefined
    emit('update', '')
}
</script>
<template>
  <div
    class="formkit-outer"
    :data-invalid="isInvalid"
  >
    <FormKit
      ref="input"
      v-model="formattedDate"
      :name="props.data.name"
      :validation="isRequired ? 'required' : undefined"
      type="hidden"
    />

    <div
      ref="datepickerWrapper"
      class="position-relative"
    >
      <FormKitCustomDateResetBtn
        v-if="props.showResetBtn && model"
        @click.prevent="handleClickReset"
      />
      <img
        v-else
        src="~/assets/icons/calendar2.svg"
        width="18"
        height="19"
        loading="lazy"
        decoding="async"
        alt=""
        class="d-block position-absolute top-50 end-0 translate-middle-y me-4 pe-none z-index-5"
      >

      <!-- Screen reader announcement for keyboard-focused item -->
      <div
        aria-live="polite"
        aria-atomic="true"
        class="visually-hidden"
      >
        {{ screenReaderAnnouncement }}
      </div>

      <datepicker
        :id="`c-${props.data.name}`"
        v-model="model"
        :locale="sk"
        :name="`c-${props.data.name}`"
        :clearable="false"
        :aria-label="props.data.label || 'Vyberte dátum'"
        :aria-invalid="isInvalid"
        :aria-describedby="isInvalid ? `${props.data.name}-messages` : undefined"
        input-format="dd. MM. yyyy"
        :starting-view="props.startView"
        :upper-limit="props.upperLimit"
        :lower-limit="props.lowerLimit"
        :class="{
          'open': opened || Boolean(model),
        }"
        @blur="userInteracted = true"
        @opened="opened = true"
        @closed="opened = false"
        @update:model-value="handleChange"
      />

      <label
        v-if="props.data.label"
        :for="`c-${props.data.name}`"
        class="position-absolute top-50 translate-middle-y fs-smaller d-block transition"
        :class="{
          'open': opened || Boolean(model),
        }"
      >
        {{ props.data.label }}
        <template
          v-if="
            isRequired
          "
        >
          *
        </template>
      </label>
    </div>
    <ul
      :id="`${props.data.name}-messages`"
      class="formkit-messages"
      :role="isInvalid ? 'alert' : undefined"
      :aria-live="isInvalid ? 'assertive' : undefined"
    >
      <li
        v-if="isInvalid"
        data-message-type="validation"
        class="formkit-message ms-3"
      >
        {{ props.data.label }} {{ $t('je povinný údaj') }}
      </li>
    </ul>
  </div>
</template>

<style lang="scss">
@import '~/components/form-kit/wrapper/FormKitWrapper.vars.scss';
@import './FormKitCustomDate.scss';
</style>
